import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit, History } from "lucide-react";
import IngredientModal from "@/components/IngredientModal";
import LogPriceModal from "@/components/LogPriceModal";
import BaseCurrencySetup from "@/components/BaseCurrencySetup";
import { formatCurrency } from "@/lib/currencyUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useTranslation } from "react-i18next";
import usePageTitle from "@/hooks/usePageTitle";

const PantryDashboard = ({ settings, setSettings }) => {
  usePageTitle("pageTitles.pantry");
  const { t } = useTranslation();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] = useState(null);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [ingredientForPrice, setIngredientForPrice] = useState(null);

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
      toast.error(t("toasts.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddIngredient = () => {
    setEditingIngredient(null);
    setIsModalOpen(true);
  };

  const handleEditIngredient = (ingredient) => {
    setEditingIngredient(ingredient);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (ingredient) => {
    setIngredientToDelete(ingredient);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ingredientToDelete) return;

    try {
      await axios.delete(`${API}/ingredients/${ingredientToDelete.id}`);
      toast.success(t("toasts.ingredientDeleted"));
      fetchIngredients();
    } catch (error) {
      console.error("Error deleting ingredient:", error);
      toast.error(t("toasts.ingredientFailed"));
    } finally {
      setDeleteDialogOpen(false);
      setIngredientToDelete(null);
    }
  };

  const handleModalClose = (shouldRefresh) => {
    setIsModalOpen(false);
    setEditingIngredient(null);
    if (shouldRefresh) {
      fetchIngredients();
    }
  };

  const currency = settings?.currency || "USD";

  return (
    <div className="min-h-screen bg-slate-950" data-testid="pantry-dashboard">
      <Toaster position="top-right" />
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 🆕 One-time kitchen-currency setup (auto-hides when base_currency is set or dismissed) */}
        <BaseCurrencySetup
          settings={settings}
          onSaved={(updated) => setSettings?.(updated)}
        />

        {/* Personalized Welcome */}
        {settings?.full_name ? (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white">
              {settings.preferred_language === 'es' && `¡Bienvenido, Chef ${settings.full_name.split(' ')[0]}! 👨‍🍳`}
              {settings.preferred_language === 'fr' && `Bienvenue, Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
              {settings.preferred_language === 'de' && `Willkommen, Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
              {settings.preferred_language === 'it' && `Benvenuto, Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
              {settings.preferred_language === 'pt' && `Bem-vindo, Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
              {settings.preferred_language === 'ar' && `مرحباً، Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
              {settings.preferred_language === 'zh' && `欢迎，Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
              {settings.preferred_language === 'ja' && `ようこそ、Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
              {settings.preferred_language === 'hi' && `स्वागत है, Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
              {(!settings.preferred_language || settings.preferred_language === 'en') && `Welcome, Chef ${settings.full_name.split(' ')[0]} 👨‍🍳`}
            </h1>
            <p className="text-slate-400 mt-2">
              {settings.country && `${settings.preferred_language === 'es' ? 'Cocinando desde' : settings.preferred_language === 'fr' ? 'Cuisine depuis' : settings.preferred_language === 'de' ? 'Kochen aus' : settings.preferred_language === 'it' ? 'Cucina da' : settings.preferred_language === 'pt' ? 'Cozinhando de' : 'Cooking from'} ${settings.country}`}
              {settings.subscription_tier && ` • ${settings.subscription_tier === 'individual' ? 'Individual' : 'Professional'} Plan`}
              {settings.professional_identity && ` • ${settings.professional_identity.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
            </p>
          </div>
        ) : (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white">
              Welcome, Chef! 👨‍🍳
            </h1>
            <p className="text-slate-400 mt-2">
              Ready to manage your pantry
            </p>
          </div>
        )}
        
        {/* Action Bar */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">{t("pantry.title")}</h2>
            <p className="text-slate-400 mt-1">
              {t("pantry.ingredientsTracked", { count: ingredients.length })}
            </p>
          </div>
          <Button
            onClick={handleAddIngredient}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            data-testid="add-ingredient-button"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t("pantry.addIngredient")}
          </Button>
        </div>

        {/* Ingredients Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-slate-400 text-lg">{t("common.loading")}</div>
          </div>
        ) : ingredients.length === 0 ? (
          <div className="text-center py-12 bg-slate-900/50 rounded-lg border border-slate-800">
            <div className="text-slate-400 text-lg mb-4">{t("pantry.noIngredients")}</div>
            <p className="text-slate-500 mb-6">
              {t("pantry.noIngredientsDesc")}
            </p>
            <Button
              onClick={handleAddIngredient}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            >
              <Plus className="h-5 w-5 mr-2" />
              {t("pantry.addFirstIngredient")}
            </Button>
          </div>
        ) : (
          <div className="bg-slate-900/50 rounded-lg border border-slate-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="ingredients-table">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-900">
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("pantry.ingredient")}
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("pantry.category")}
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("pantry.bulkPrice")}
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("pantry.bulkQty")}
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("pantry.unitCost")}
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("pantry.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ingredients.map((ingredient) => (
                    <tr
                      key={ingredient.id}
                      className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors"
                      data-testid={`ingredient-row-${ingredient.id}`}
                    >
                      <td className="py-4 px-6">
                        <div className="font-medium text-white">{ingredient.name}</div>
                        {ingredient.notes && (
                          <div className="text-sm text-slate-400 mt-1">{ingredient.notes}</div>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20">
                          {ingredient.category}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right font-mono text-white">
                        {formatCurrency(ingredient.bulk_price, currency)}
                      </td>
                      <td className="py-4 px-6 text-right text-slate-300">
                        {ingredient.bulk_quantity} {ingredient.unit}
                      </td>
                      <td className="py-4 px-6 text-right font-mono text-amber-500 font-semibold">
                        {formatCurrency(ingredient.unit_cost, currency, 6)}
                        <span className="text-slate-500 text-sm ml-1">/{ingredient.unit}</span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setIngredientForPrice(ingredient);
                              setPriceModalOpen(true);
                            }}
                            className="hover:bg-slate-800 text-slate-400 hover:text-emerald-400"
                            title="Log new price"
                            data-testid={`log-price-ingredient-${ingredient.id}`}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditIngredient(ingredient)}
                            className="hover:bg-slate-800 text-slate-400 hover:text-amber-500"
                            data-testid={`edit-ingredient-${ingredient.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(ingredient)}
                            className="hover:bg-red-500/10 text-slate-400 hover:text-red-500"
                            data-testid={`delete-ingredient-${ingredient.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Ingredient Modal */}
      <IngredientModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        ingredient={editingIngredient}
        currency={currency}
      />

      {/* Price Ledger Modal */}
      <LogPriceModal
        isOpen={priceModalOpen}
        onClose={() => {
          setPriceModalOpen(false);
          setIngredientForPrice(null);
        }}
        ingredient={ingredientForPrice}
        currency={currency}
        onLogged={() => {
          // refresh ingredients so cached unit_cost reflects the new entry
          fetchIngredients();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t("deleteDialog.ingredientTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {t("deleteDialog.ingredientDesc", { name: ingredientToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-button"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PantryDashboard;
